import React, { useState, useEffect } from 'react'

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s'
}

const labelStyle = {
  display: 'block', fontFamily: 'monospace',
  fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--text-muted)', marginBottom: '0.35rem'
}

export default function AddInsuranceModal({ policy, onSave, onClose }) {
  const [form, setForm] = useState({
    name: '', type: 'health', provider: '', policy_number: '',
    premium_amount: '', premium_frequency: 'yearly', due_day: '',
    start_date: '', expiry_date: '', sum_assured: '', nominee: '', notes: ''
  })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (policy) {
      setForm({
        name: policy.name || '',
        type: policy.type || 'health',
        provider: policy.provider || '',
        policy_number: policy.policy_number || '',
        premium_amount: policy.premium_amount || '',
        premium_frequency: policy.premium_frequency || 'yearly',
        due_day: policy.due_day || '',
        start_date: policy.start_date ? policy.start_date.slice(0, 10) : '',
        expiry_date: policy.expiry_date ? policy.expiry_date.slice(0, 10) : '',
        sum_assured: policy.sum_assured || '',
        nominee: policy.nominee || '',
        notes: policy.notes || ''
      })
    }
  }, [policy])

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Please enter a policy name'); return }
    if (!form.premium_amount || isNaN(form.premium_amount)) { setError('Please enter a valid premium amount'); return }

    setSaving(true)
    setError('')
    await onSave({
      name: form.name.trim(),
      type: form.type,
      provider: form.provider.trim() || null,
      policy_number: form.policy_number.trim() || null,
      premium_amount: parseFloat(form.premium_amount),
      premium_frequency: form.premium_frequency,
      due_day: form.due_day ? parseInt(form.due_day) : null,
      start_date: form.start_date || null,
      expiry_date: form.expiry_date || null,
      sum_assured: form.sum_assured ? parseFloat(form.sum_assured) : null,
      nominee: form.nominee.trim() || null,
      notes: form.notes.trim() || null,
    })
    setSaving(false)
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0,
        background: 'var(--modal-backdrop)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem'
      }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: '10px',
        border: '1px solid var(--border)',
        width: '100%', maxWidth: '560px',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)'
        }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.15rem', fontWeight: '700' }}>
            {policy ? 'Edit Policy' : 'Add Insurance Policy'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1, padding: '0.2rem' }}>✕</button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          {error && (
            <div style={{ background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.7rem 1rem', marginBottom: '1rem', fontSize: '0.82rem', color: '#b8441a' }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Policy Name</label>
              <input style={inputStyle} placeholder="e.g. LIC Jeevan Anand" value={form.name}
                onChange={e => set('name', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div>
              <label style={labelStyle}>Insurance Type</label>
              <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="life">Life</option>
                <option value="health">Health</option>
                <option value="vehicle">Vehicle</option>
                <option value="home">Home</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Provider / Insurer</label>
              <input style={inputStyle} placeholder="e.g. LIC, Star Health" value={form.provider}
                onChange={e => set('provider', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Policy Number</label>
              <input style={inputStyle} placeholder="e.g. LIC-123456789" value={form.policy_number}
                onChange={e => set('policy_number', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div>
              <label style={labelStyle}>Premium Amount (₹)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="12000" value={form.premium_amount}
                onChange={e => set('premium_amount', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div>
              <label style={labelStyle}>Premium Frequency</label>
              <select style={inputStyle} value={form.premium_frequency} onChange={e => set('premium_frequency', e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="half-yearly">Half-Yearly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>Premium Due Day</label>
              <select style={inputStyle} value={form.due_day} onChange={e => set('due_day', e.target.value)}>
                <option value="">-- Select Day --</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Sum Assured (₹)</label>
              <input style={inputStyle} type="text" inputMode="decimal" placeholder="1000000" value={form.sum_assured}
                onChange={e => set('sum_assured', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div>
              <label style={labelStyle}>Start Date</label>
              <input style={inputStyle} type="date" value={form.start_date}
                onChange={e => set('start_date', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div>
              <label style={labelStyle}>Expiry / Renewal Date</label>
              <input style={inputStyle} type="date" value={form.expiry_date}
                onChange={e => set('expiry_date', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Nominee</label>
              <input style={inputStyle} placeholder="e.g. Priya Sharma" value={form.nominee}
                onChange={e => set('nominee', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            <div style={{ gridColumn: '1/-1' }}>
              <label style={labelStyle}>Notes</label>
              <textarea
                style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
                placeholder="Any additional notes about this policy..."
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--text)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>
          </div>
        </div>

        <div style={{ padding: '0.9rem 1.5rem 1.3rem', display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.5rem 1rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving...' : 'Save Policy'}
          </button>
        </div>
      </div>
    </div>
  )
}
